import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UploadCloud } from "lucide-react";
import { useApi, fmtDate, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { StatusBadge, ConfidencePill } from "@/components/Badges";
import { SourceImage } from "@/components/SourceImage";
import { t } from "@/i18n";
import { Tt, titleOf } from "@/components/Tt";
import { TagChips, TAG_META, tagsOf } from "@/components/AudiencePanel";

const TABS = [["", "All"], ["published", "Published"], ["draft", "Drafts"], ["outdated", "Outdated"]];

export default function LibraryPage() {
  const api = useApi();
  const { me } = useSession();
  const [sops, setSops] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("");

  useEffect(() => {
    api.get("/sops", { params: tab ? { status: tab } : {} }).then((r) => setSops(r.data));
  }, [api, tab]);

  const [tagF, setTagF] = useState([]);
  const want = tagF.filter((x) => x !== "too_open");
  const [retagging, setRetagging] = useState(false);
  const list = (sops || []).filter((s) => `${s.title} ${titleOf(s.title)}`.toLowerCase().includes(q.toLowerCase()))
    .filter((s) => (!want.length || want.some((x) => tagsOf(s).includes(x))) && (!tagF.includes("too_open") || s.too_open));
  const flip = (k) => setTagF((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const retag = async () => {
    setRetagging(true);
    try {
      const { data } = await api.post("/sops/retag-review");
      toast.success(t("Checked {n} procedures · {m} may be too open", { n: data.checked, m: data.too_open.length }));
      const r = await api.get("/sops", { params: tab ? { status: tab } : {} });
      setSops(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setRetagging(false);
  };
  return (
    <div className="page stack">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("Library")}</h1>
        {me.is_maintainer && <Link to="/capture" className="btn btn-primary" data-testid="library-new-capture-button"><UploadCloud size={16} /> {t("New capture")}</Link>}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 faint" />
          <input className="field !pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search procedures")} data-testid="library-search-input" />
        </div>
        {me.is_maintainer && TABS.map(([v, l]) => (
          <button key={v} className={`nav-link ${tab === v ? "active" : ""}`} onClick={() => setTab(v)} data-testid={`library-tab-${l.toLowerCase()}`}>{t(l)}</button>
        ))}
        {me.is_maintainer && (
          <>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="library-tag-filter">
              {["customer", "team", "cashier", ...(me.role === "admin" ? ["private"] : []), "too_open"].map((k) => {
                const m = TAG_META[k];
                const Icon = m?.icon;
                const on = tagF.includes(k);
                return <button key={k} aria-pressed={on} onClick={() => flip(k)} className={`chip cursor-pointer transition-opacity ${m ? m.cls : "chip-warn"} ${on ? "ring-2 ring-[var(--accent-primary)]" : "opacity-60 hover:opacity-100"}`} data-testid={`library-tag-filter-${k}`}>
                  {Icon && <Icon size={11} />} {t(m ? m.label : "Too open")}
                </button>;
              })}
              {!!tagF.length && <button className="text-xs faint underline" onClick={() => setTagF([])} data-testid="library-tag-filter-clear">{t("Clear")}</button>}
            </div>
            <button className="btn btn-ghost btn-sm" disabled={retagging} onClick={retag} data-testid="library-retag-button">{t(retagging ? "Checking…" : "Re-check all tags")}</button>
          </>
        )}
      </div>
      {sops === null ? <p className="faint">{t("Loading…")}</p> : list.length === 0 ? (
        <div className="card text-center py-14" data-testid="library-empty"><p className="font-semibold">{t("No procedures yet.")}</p><p className="text-sm faint mt-1">{t(me.is_maintainer ? "Capture your first one to get started." : "Your owner hasn't published any procedures yet.")}</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-s">
          {list.map((s, i) => (
            <Link key={s.id} to={`/sops/${s.id}`} className={`card card-hover stack-sm rise d${(i % 4) + 1} ${s.status === "published" && s.cover_source_id ? "!pt-0 overflow-hidden" : ""}`} data-testid={`library-sop-card-${s.id}`}>
              {s.status === "published" && s.cover_source_id && <SourceImage sourceId={s.cover_source_id} className="-mx-6 w-[calc(100%+3rem)] max-w-none h-44" testid={`library-cover-${s.id}`} />}
              <div className="flex flex-wrap gap-1.5"><StatusBadge status={s.status} /><ConfidencePill level={s.confidence} />
                {s.open_flags.includes("contradiction") && <span className="chip chip-warn">{t("Contradiction")}</span>}
                {s.stale && <span className="chip chip-warn">{t("Stale")}</span>}
                {me.is_maintainer && <TagChips tags={s.tags} testid={`library-tags-${s.id}`} />}
                {s.too_open && <span className="chip chip-warn" data-testid={`library-too-open-${s.id}`}>{t("Too open")}</span>}
                {me.is_maintainer && s.open_clarifications > 0 && <span className="chip chip-info">{t("{n} to clarify", { n: s.open_clarifications })}</span>}
              </div>
              <h3 className="font-bold text-lg leading-snug"><Tt text={s.title} /></h3>
              <p className="text-xs faint">{t("Updated")} {fmtDate(s.updated_at)} · {t("Sources: {n}", { n: s.source_ids.length })}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
